import { api, getToken } from "@/lib/api/client";
import type { Content, ContentInput, RelatedKind, RelatedOption } from "./types";

/**
 * Las portadas se suben por el endpoint de los portales (convierte a webp y
 * deja la imagen pública): el servicio de Nexus no escribe en los buckets.
 * En local, VITE_UPLOAD_BASE apunta al backend local.
 */
const UPLOAD_BASE = import.meta.env.VITE_UPLOAD_BASE || "https://api.mixcore.club";
export const COVER_MAX_MB = 8;

/**
 * Cliente del CMS del feed cultural (Docs/plan-comercial/03b).
 * Endpoints admin en mn-server: /api/admin/content (JwtAuth + content:*).
 */
export const contentApi = {
  list: async (params: { status?: string; type?: string }): Promise<Content[]> => {
    const { data } = await api.get<Content[]>("/api/admin/content", { params });
    return data;
  },
  get: async (id: number): Promise<Content> => {
    const { data } = await api.get<Content>(`/api/admin/content/${id}`);
    return data;
  },
  create: async (body: ContentInput): Promise<Content> => {
    const { data } = await api.post<Content>("/api/admin/content", body);
    return data;
  },
  update: async (id: number, body: Partial<ContentInput>): Promise<Content> => {
    const { data } = await api.put<Content>(`/api/admin/content/${id}`, body);
    return data;
  },
  // publishAt (ISO, futura) = programar; sin publishAt = publicar ya.
  publish: async (id: number, publish: boolean, publishAt?: string): Promise<Content> => {
    const body = publishAt ? { publish, publishAt } : { publish };
    const { data } = await api.post<Content>(`/api/admin/content/${id}/publish`, body);
    return data;
  },
  relatedSearch: async (kind: RelatedKind, q: string): Promise<RelatedOption[]> => {
    const { data } = await api.get<{ items: RelatedOption[] }>("/api/admin/content/related-search", {
      params: { kind, q },
    });
    return data.items;
  },
  uploadCover: async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = getToken();
    const res = await fetch(`${UPLOAD_BASE}/storage/upload-image`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.blobUrl) throw new Error(data?.error || "No se pudo subir la imagen.");
    return data.blobUrl as string;
  },
  feature: async (id: number, days: number): Promise<Content> => {
    const { data } = await api.post<Content>(`/api/admin/content/${id}/feature`, { days });
    return data;
  },
  remove: async (id: number): Promise<void> => {
    await api.delete(`/api/admin/content/${id}`);
  },
};

export type ReviewItem = {
  id: number;
  author: string;
  review: string | null;
  photos: string[];
  tags: string[];
  eventId: number | null;
  eventName: string | null;
  moderation_status: string;
  createdAt: string;
};

/** Moderación de reseñas públicas (VibeCheck) — Docs/plan-comercial/03b. */
export const reviewsApi = {
  queue: async (status: string): Promise<ReviewItem[]> => {
    const { data } = await api.get<{ total: number; reviews: ReviewItem[] }>(
      "/vibe-check/moderation/queue",
      { params: { status } },
    );
    return data.reviews;
  },
  moderate: async (id: number, action: "approve" | "reject"): Promise<void> => {
    await api.post(`/vibe-check/moderation/${id}`, { action });
  },
};
